import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { PLAY_STORE_URL } from "../../lib/billing";
import AdminUsers from "./AdminUsers";
import AdminNotifiche from "./AdminNotifiche";
import AdminReviews from "./AdminReviews";
import AdminContent from "./AdminContent";
import AdminVirginia from "./AdminVirginia";
import AdminReports from "./AdminReports";
import AdminFestivita from "./AdminFestivita";
import AdminPosts from "./AdminPosts";
import AdminPush from "./AdminPush";
import AdminMetodi from "./AdminMetodi";

// Voci principali del pannello, nell'ordine stabilito dalla proprietaria.
// "Contenuti e Contatti" è mantenuta perché modifica recapiti e testi usati
// dalle pagine pubbliche (Home, Chi Sono): senza di essa non sarebbero più modificabili.
const TABS = [
  { value: "utenti", label: "Utenti", Component: AdminUsers },
  { value: "virginia", label: "Domande per Virginia", Component: AdminVirginia },
  { value: "recensioni", label: "Recensioni", Component: AdminReviews },
  { value: "avvisi", label: "Avvisi", Component: AdminNotifiche },
  { value: "segnalazioni", label: "Segnalazioni", Component: AdminReports },
  { value: "push", label: "Notifiche push", Component: AdminPush },
  { value: "festivita", label: "Festività", Component: AdminFestivita },
  { value: "metodi", label: "Metodi", Component: AdminMetodi },
  { value: "contenuti", label: "Contenuti e Contatti", Component: AdminContent },
];

// Schede senza voce nella barra, raggiungibili solo dai link diretti:
// la gestione di "Oltre le Carte" avviene dalla pagina pubblica /oltre-le-carte
// (barra admin "Scrivi un post" e matite di modifica portano a ?tab=oltre).
const EXTRA_TABS = [
  { value: "oltre", Component: AdminPosts },
];

const ALL_TABS = [...TABS, ...EXTRA_TABS];

// Vecchi valori di scheda (eventuali link salvati): i mazzi ora sono dentro "Metodi",
// "servizi" è stata rimossa dal pannello.
const LEGACY_TAB_MAP = {
  servizi: "utenti",
  sibilla: "metodi", tarocchi: "metodi", napoletane: "metodi", stesa26: "metodi",
  piacentine: "metodi", oracolo: "metodi", rune: "metodi",
};

const resolveTab = (t) => {
  if (!t) return null;
  if (ALL_TABS.some((x) => x.value === t)) return t;
  return LEGACY_TAB_MAP[t] || null;
};

export default function Admin() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => resolveTab(params.get("tab")) || "utenti");
  useEffect(() => {
    const t = resolveTab(params.get("tab"));
    if (t) setTab(t);
  }, [params]);

  // Condivide SOLO il link pubblico per scaricare l'app da Google Play (mai dati dell'account):
  // su mobile si apre la finestra di sistema con WhatsApp, Telegram, SMS e le altre app.
  const shareApp = async () => {
    const text = "Arcana — Antica cartomanzia & Oracoli. Scarica l'app da Google Play:";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Arcana", text, url: PLAY_STORE_URL });
      } else {
        await navigator.clipboard.writeText(`${text} ${PLAY_STORE_URL}`);
        toast.success("Link copiato: incollalo dove vuoi condividere l'app.");
      }
    } catch (e) { /* condivisione annullata dall'utente */ }
  };
  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12 sm:py-16" data-testid="admin-page">
      <div className="mb-10">
        <p className="eyebrow">Area Riservata</p>
        <h1 className="font-display text-4xl text-amber-100 mt-3">Pannello di Virginia</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start flex-wrap bg-[#180a26] border border-[#4a2175] h-auto" data-testid="admin-tabs-list">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`admin-tab-${t.value}`}
              className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-purple-300/60 text-xs sm:text-sm whitespace-nowrap"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {ALL_TABS.map(({ value, Component }) => (
          <TabsContent key={value} value={value} className="mt-8">
            <Component />
          </TabsContent>
        ))}
      </Tabs>
      <div className="mt-14 flex justify-center">
        <button
          onClick={shareApp}
          data-testid="admin-share-app-btn"
          className="text-xs text-purple-300/50 hover:text-amber-300 transition-colors inline-flex items-center gap-1.5"
        >
          <Share2 className="w-3.5 h-3.5" /> Condividi app
        </button>
      </div>
    </div>
  );
}
